<template>
  <div class="db-root">

    <!-- header rail, same ops rail language as vsm2: eyebrow +
         title on the left, board date + manual refresh on the right -->
    <div class="db-rail">
      <div class="db-rail-text">
        <div class="db-rail-eyebrow">Ops &middot; Departure Board Preview</div>
        <h3 class="db-rail-title">
          Departures
          <FiFavStar />
          <span v-if="contextTenantName" class="db-rail-tenant-chip">{{ contextTenantName }}</span>
        </h3>
        <div v-if="summary" class="db-rail-sub">
          {{ items_full.length }} vehicles &middot; {{ boardDateLabel }}<span v-if="summary.timezone"> &middot; {{ summary.timezone }}</span>
        </div>
      </div>
      <div class="db-rail-actions">
        <b-form-input
          v-model="board_date"
          type="date"
          size="sm"
          class="db-date-input"
          aria-label="Board date"
          @change="load_data"
        />
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="refreshing"
          title="Refresh"
          aria-label="Refresh"
          @click="load_data"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': refreshing }"></i>
        </b-button>
        <b-button
          v-if="summary"
          size="sm"
          variant="outline-secondary"
          title="Waiting room display"
          aria-label="Open the waiting room display"
          @click="enterDisplay()"
        >
          <i class="fa fa-television"></i>
        </b-button>
      </div>
    </div>

    <div class="db-note">Staff preview of the departure board redesign. Customers keep the current page while we iterate here.</div>

    <!-- load failures surface inline so rows already on screen stay put -->
    <div v-if="error" class="db-error">
      <span>{{ error }}</span>
      <b-button size="sm" variant="outline-secondary" @click="load_data">Retry</b-button>
    </div>

    <!-- the board is sourced from the telematics feed, so a fleet with no
         connected provider comes back 404/empty. state that plainly instead
         of firing the retryable error banner. -->
    <div v-if="noData" class="db-no-telematics">
      <i class="fa fa-plug" aria-hidden="true"></i>
      <div class="db-no-telematics-title">No departure data for this fleet</div>
      <div class="db-no-telematics-sub">Nothing is scheduled to show here yet. If this tenant has no telematics integration, switch to one with a connected fleet (Samsara, Zonar, and the like) to preview the board.</div>
    </div>

    <template v-else>
    <!-- departure-window chips: multi-select OR within the row, counts
         always read from the unfiltered set so they stay stable -->
    <div class="db-chip-filters" role="group" aria-label="Departure window filters">
      <button
        v-for="chip in windowChips"
        :key="chip.value"
        type="button"
        :class="['db-chip', { 'db-chip--active': window_filters.includes(chip.value) }]"
        :aria-pressed="window_filters.includes(chip.value) ? 'true' : 'false'"
        @click="toggleWindowFilter(chip.value)"
      >{{ chip.label }} ({{ chip.count }})</button>
    </div>

    <!-- wall displays run this board on tvs; the tenant font size scales
         the table from the card so every cell inherits it -->
    <div class="db-card" :style="tableFontStyle">
      <b-table
        :busy="loading"
        :items="filteredItems"
        :fields="visibleFields"
        :sort-compare="tableSortCompare"
        responsive
        show-empty
      >

        <template slot="table-busy">
          <div class="db-busy"><i class="fa fa-spinner fa-spin"></i>&nbsp;Loading departures...</div>
        </template>

        <template slot="empty">
          <div class="db-empty">
            <template v-if="filtersActive">
              0 of {{ items_full.length }} vehicles match these filters.
              <button type="button" class="db-clear-btn" @click="clearFilters">Clear filters</button>
            </template>
            <template v-else>
              No departures found for {{ emptyDateLabel }}.
              <b-button size="sm" variant="outline-secondary" class="db-empty-reload" @click="load_data">Reload</b-button>
            </template>
          </div>
        </template>

        <!-- the four movement columns stack one line per movement so a
             vehicle's extra bookings stay aligned across the row -->
        <template slot="next_mvmt_id" slot-scope="row">
          <span class="db-mvmt-line">{{ row.item.next_mvmt_id || '-' }}</span>
          <span v-for="(nm, idx) in row.item.next_mvmt_list || []" :key="'id-' + idx" class="db-mvmt-line">{{ nm.next_mvmt_id || '-' }}</span>
        </template>

        <template slot="next_mvmt_driver" slot-scope="row">
          <span class="db-mvmt-line">{{ driverLabel(row.item.next_mvmt_driver) }}</span>
          <span v-for="(nm, idx) in row.item.next_mvmt_list || []" :key="'dr-' + idx" class="db-mvmt-line">{{ driverLabel(nm.next_mvmt_driver) }}</span>
        </template>

        <template slot="next_mvmt_time" slot-scope="row">
          <span class="db-mvmt-line" :title="row.item.next_mvmt_time">{{ formatStamp(row.item.next_mvmt_time) }}</span>
          <span v-for="(nm, idx) in row.item.next_mvmt_list || []" :key="'tm-' + idx" class="db-mvmt-line" :title="nm.next_mvmt_time">{{ formatStamp(nm.next_mvmt_time) }}</span>
        </template>

        <template slot="next_mvmt_delta" slot-scope="row">
          <span class="db-mvmt-line">
            <span v-if="row.item.next_mvmt_delta" class="db-delta-chip" :class="deltaChipClass(row.item.next_mvmt_ms)">{{ row.item.next_mvmt_delta }}</span>
            <template v-else>-</template>
          </span>
          <span v-for="(nm, idx) in row.item.next_mvmt_list || []" :key="'dl-' + idx" class="db-mvmt-line">
            <span v-if="nm.next_mvmt_delta" class="db-delta-chip" :class="deltaChipClass(nm.next_mvmt_ms)">{{ nm.next_mvmt_delta }}</span>
            <template v-else>-</template>
          </span>
        </template>

        <!-- long locations truncate to one line, full text on the tooltip -->
        <template slot="current_location" slot-scope="row">
          <span class="db-trunc" :title="row.item.current_location">{{ row.item.current_location || '-' }}</span>
        </template>

        <template slot="next_mvmt_pickup" slot-scope="row">
          <span class="db-trunc" :title="row.item.next_mvmt_pickup">{{ row.item.next_mvmt_pickup || '-' }}</span>
        </template>

        <template slot="next_mvmt_destination" slot-scope="row">
          <span class="db-trunc" :title="row.item.next_mvmt_destination">{{ row.item.next_mvmt_destination || '-' }}</span>
        </template>

      </b-table>
    </div>
    </template>

    <!-- waiting room display: fixed overlay styled for tvs in passenger
         lounges. entered from the tv button or a ?display=<theme> kiosk
         bookmark; esc or the auto-hiding control pill exits -->
    <div
      v-if="display_mode"
      ref="display_root"
      :class="['db-display', 'db-display--' + display_theme, { 'db-display--idle': display_idle, 'db-display--no-trip': !displayHasGroups }]"
      role="region"
      aria-label="Departures display"
      @mousemove="wakeControls"
      @click="wakeControls"
    >
      <div class="db-dp-frame">
        <header class="db-dp-header">
          <div class="db-dp-head-left">
            <div v-if="displayOperatorName" class="db-dp-operator">{{ displayOperatorName }}</div>
            <h1 class="db-dp-title">Departures</h1>
          </div>
          <div class="db-dp-head-right">
            <div v-if="isSolari" class="db-dp-clock db-dp-clock--flap"><split_flap :value="displayClock" :length="8"/></div>
            <div v-else class="db-dp-clock">{{ displayClock }}</div>
            <div class="db-dp-date">{{ displayClockDate }}<span v-if="summary && summary.timezone" class="db-dp-tz"> &middot; {{ summary.timezone }}</span></div>
          </div>
        </header>

        <div ref="display_board" class="db-dp-board">
          <!-- solari: true animated split-flap lines; rows are keyed by
               position (not trip) so page flips flutter in place -->
          <template v-if="isSolari">
            <div class="db-dp-row--head sf-head" aria-hidden="true">
              <span class="sf-col sf-col-time"><span class="sf-head-label">Departs</span></span>
              <span v-if="displayHasGroups" class="sf-col sf-col-group"><span class="sf-head-label">Trip</span></span>
              <span class="sf-col sf-col-dest"><span class="sf-head-label">Destination</span></span>
              <span class="sf-col sf-col-pickup db-dp-cell-pickup"><span class="sf-head-label">Picks up at</span></span>
              <span class="sf-col sf-col-coach"><span class="sf-head-label">Coach</span></span>
              <span class="sf-col sf-col-status"><span class="sf-head-label">Status</span></span>
            </div>
            <div class="db-dp-rows db-dp-rows--solari">
              <div
                v-for="(trip, idx) in solariRows"
                :key="'sfr-' + idx"
                class="sf-row"
                :class="{ 'db-dp-row--next': page_index === 0 && idx === 0 && trip.urgency !== 'blank' }"
              >
                <span class="sf-col sf-col-time"><split_flap :value="sfTime(trip)" :length="9"/></span>
                <span v-if="displayHasGroups" class="sf-col sf-col-group"><split_flap :value="trip.group === '-' ? '' : trip.group" :length="12"/></span>
                <span class="sf-col sf-col-dest"><split_flap :value="trip.destination === '-' ? '' : trip.destination" :length="13"/></span>
                <span class="sf-col sf-col-pickup db-dp-cell-pickup"><split_flap :value="trip.pickup === '-' ? '' : trip.pickup" :length="12"/></span>
                <span class="sf-col sf-col-coach"><split_flap :value="trip.coach === '-' ? '' : trip.coach" :length="8"/></span>
                <span class="sf-col sf-col-status" :class="'sf-status--' + trip.urgency"><split_flap :value="trip.status" :length="10"/></span>
              </div>
              <div v-if="!displayRows.length && !loading" class="db-dp-quiet db-dp-quiet--solari">
                <div class="db-dp-quiet-title">No departures scheduled</div>
                <div class="db-dp-quiet-sub">Please see a staff member for the latest trip information.</div>
              </div>
            </div>
          </template>

          <template v-else>
          <div class="db-dp-row db-dp-row--head" aria-hidden="true">
            <span>Departs</span>
            <span class="db-dp-cell-trip">Trip</span>
            <span>Destination</span>
            <span class="db-dp-cell-pickup">Picks up at</span>
            <span>Coach</span>
            <span class="db-dp-cell-status">Status</span>
          </div>
          <div :key="'page-' + page_index + '-' + last_updated_at" class="db-dp-rows">
            <div
              v-for="(trip, idx) in currentPageRows"
              :key="trip.key"
              class="db-dp-row"
              :class="{ 'db-dp-row--next': page_index === 0 && idx === 0 }"
              :style="{ animationDelay: (idx * 55) + 'ms' }"
            >
              <span class="db-dp-cell-time"><span v-if="trip.timeDay" class="db-dp-time-day">{{ trip.timeDay }}</span>{{ trip.time }}</span>
              <span class="db-dp-cell-trip">{{ trip.group }}</span>
              <span class="db-dp-cell-dest">{{ trip.destination }}</span>
              <span class="db-dp-cell-pickup">{{ trip.pickup }}</span>
              <span class="db-dp-cell-coach">{{ trip.coach }}</span>
              <span class="db-dp-cell-status"><span class="db-dp-status" :class="'db-dp-status--' + trip.urgency">{{ trip.status }}</span></span>
            </div>
            <div v-if="!displayRows.length && !loading" class="db-dp-quiet">
              <div class="db-dp-quiet-title">No departures scheduled</div>
              <div class="db-dp-quiet-sub">Please see a staff member for the latest trip information.</div>
            </div>
          </div>
          </template>
        </div>

        <!-- thin progress bar telegraphs the next page flip; the 12s
             animation matches the page_timer interval -->
        <div v-if="pageCount > 1" class="db-dp-rotbar" aria-hidden="true">
          <div :key="'rot-' + page_index" class="db-dp-rotbar-fill"></div>
        </div>

        <footer class="db-dp-footer">
          <div v-if="pageCount > 1" class="db-dp-pages" aria-hidden="true">
            <span
              v-for="p in pageCount"
              :key="'dot-' + p"
              class="db-dp-page-dot"
              :class="{ 'db-dp-page-dot--on': p - 1 === page_index }"
            ></span>
          </div>
          <div class="db-dp-brand">
            <span class="db-dp-live-dot" aria-hidden="true"></span>
            Fleet Integrations<span v-if="lastUpdatedLabel" class="db-dp-updated"> &middot; updated {{ lastUpdatedLabel }}</span>
          </div>
        </footer>
      </div>

      <!-- control pill fades out while the board idles; any mouse movement
           brings it back, and it stays put while the pointer is over it -->
      <div
        class="db-dp-controls"
        :class="{ 'db-dp-controls--hidden': !controls_visible }"
        @mouseenter="pinControls"
        @mouseleave="wakeControls"
      >
        <span class="db-dp-controls-label">Theme</span>
        <button
          v-for="t in displayThemes"
          :key="t.value"
          type="button"
          class="db-dp-theme-dot"
          :class="['db-dp-theme-dot--' + t.value, { 'db-dp-theme-dot--on': display_theme === t.value }]"
          :title="t.label"
          :aria-label="'Theme: ' + t.label"
          :aria-pressed="display_theme === t.value ? 'true' : 'false'"
          @click="setDisplayTheme(t.value)"
        ></button>
        <span class="db-dp-controls-sep"></span>
        <button type="button" class="db-dp-exit" @click="exitDisplay">Exit display</button>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { get_trip_sheet } from '../networking/apis'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import split_flap from './split_flap.vue'

// '-' placeholder for the plain-text columns rendered by b-table's
// default cell
const dash = value => {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

// column catalog: trip_sheet's keys with the redesign labels (sentence
// case, converged with vsm_board: 'Next ID', 'Next depart', 'Departs in')
const FIELDS = [
  { key: 'name', label: 'Vehicle', sortable: true },
  { key: 'next_mvmt_id', label: 'Next ID', sortable: true },
  { key: 'next_mvmt_driver', label: 'Next driver', sortable: true },
  { key: 'next_mvmt_time', label: 'Next depart', sortable: true },
  { key: 'next_mvmt_delta', label: 'Departs in', sortable: true },
  { key: 'next_mvmt_client_ref1', label: 'Customer', sortable: true, formatter: dash },
  { key: 'current_location', label: 'Current location', sortable: true },
  { key: 'next_mvmt_pickup', label: 'Pickup', sortable: true },
  { key: 'next_mvmt_destination', label: 'Destination', sortable: true },
]

// next_mvmt_ms arrives in epoch seconds despite the name (python
// timestamp()); rows with no scheduled movement carry a far-future
// sentinel, so anything past 2100 means "no time yet"
const NO_MOVEMENT_EPOCH = 4102444800

// waiting room display themes; the value doubles as the css modifier and
// the ?display= query value a kiosk bookmarks
const DISPLAY_THEMES = [
  { value: 'terminal', label: 'Terminal' },
  { value: 'solari', label: 'Solari' },
  { value: 'ember', label: 'Ember' },
  { value: 'daylight', label: 'Daylight' },
  { value: 'depot', label: 'Depot' },
]
const DISPLAY_THEME_KEY = 'db_display_theme'
const isDisplayTheme = value => DISPLAY_THEMES.some(t => t.value === value)

// display-mode fonts load lazily so the staff page never pays for them.
// fraunces (with the SOFT axis) + manrope come from the paper-and-brass
// waiting room concept in vsm-waitingroom.html at the workspace root
const DISPLAY_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=Barlow+Condensed:wght@500;600;700&family=IBM+Plex+Mono:wght@500;600&family=Share+Tech+Mono&family=Fraunces:opsz,wght,SOFT@9..144,300..700,0..100&family=Manrope:wght@400;500;600;700&display=swap'

export default {
  name: 'departure_board',
  components: { split_flap },
  metaInfo: {
    title: 'Departure Board Preview',
    titleTemplate: '%s · Fleet Integrations',
  },
  data() {
    return {
      loading: false,
      refreshing: false,
      error: '',
      // true after a 404/empty load — the fleet has no departures to show
      // (typically no telematics feed), rendered as a calm fact not an error
      noData: false,
      summary: null,
      items_full: [],
      board_date: moment().format('YYYY-MM-DD'),
      window_filters: [],
      refresh_timer: null,

      // sequence guard so only the latest in-flight fetch lands
      fetch_seq: 0,

      // waiting room display state
      display_mode: false,
      display_theme: 'terminal',
      display_idle: false,
      controls_visible: true,
      page_index: 0,
      page_size: 8,
      clock_now: Date.now(),
      minute_tick: 0,
      last_updated_at: 0,
      did_request_fullscreen: false,
      clock_timer: null,
      minute_timer: null,
      page_timer: null,
      idle_timer: null,
      resize_timer: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // admins deep-link with ?tenant=, so name the tenant whenever it
    // differs from the navbar selection
    contextTenantName() {
      if (!tenantContextDiffers(this.$route, this.user)) return ''
      return tenantDisplayName(this.user, this.tenant)
    },
    boardDateLabel() {
      const m = moment(this.board_date, 'YYYY-MM-DD')
      return m.isValid() ? m.format('ddd, MMM D') : this.board_date
    },
    emptyDateLabel() {
      const m = moment(this.board_date, 'YYYY-MM-DD')
      return m.isValid() ? m.format('MMM D') : this.board_date
    },
    filtersActive() {
      return this.window_filters.length > 0
    },
    windowChips() {
      const counts = { soon: 0, today: 0, upcoming: 0 }
      this.items_full.forEach(item => { counts[this.windowBucket(item)] += 1 })
      return [
        { value: 'soon', label: 'Departing soon', count: counts.soon },
        { value: 'today', label: 'Today', count: counts.today },
        { value: 'upcoming', label: 'Upcoming', count: counts.upcoming },
      ]
    },
    filteredItems() {
      if (this.window_filters.length === 0) return this.items_full
      return this.items_full.filter(item => this.window_filters.includes(this.windowBucket(item)))
    },
    // tenant-configured column list when present (same key filter as the
    // customer page); unknown keys drop, the full catalog is the default
    visibleFields() {
      const byKey = {}
      FIELDS.forEach(field => { byKey[field.key] = field })
      if (this.summary && this.summary.visible_fields) {
        const keys = this.summary.visible_fields.split(',').map(k => k.trim()).filter(k => byKey[k])
        if (keys.length) return keys.map(k => byKey[k])
      }
      return FIELDS
    },
    // tenant wall-display font size, clamped so a bad value cannot break
    // the layout and an absent one falls back instead of "undefinedpx"
    tableFontStyle() {
      const parsed = parseInt(this.summary && this.summary.font_size, 10)
      const size = isNaN(parsed) ? 13 : Math.min(20, Math.max(11, parsed))
      return { fontSize: size + 'px' }
    },

    displayThemes() {
      return DISPLAY_THEMES
    },
    displayOperatorName() {
      return tenantDisplayName(this.user, this.tenant) || ''
    },
    displayClock() {
      const m = this.displayMoment(this.clock_now)
      return m.format('h:mm A')
    },
    displayClockDate() {
      const m = this.displayMoment(this.clock_now)
      return m.format('dddd, MMMM D')
    },
    lastUpdatedLabel() {
      if (!this.last_updated_at) return ''
      return this.displayMoment(this.last_updated_at).format('h:mma').replace(/(a|p)m$/, '$1')
    },
    // one row per scheduled trip for the passenger board: the head
    // movement plus each stacked booking, departed and unscheduled rows
    // dropped, soonest first. dispatch details (driver, current location)
    // stay off the public surface
    displayRows() {
      void this.minute_tick // re-derive countdown labels on the tick
      const rows = []
      this.items_full.forEach(item => {
        rows.push(this.displayTrip(item, item, 0))
        const extras = item.next_mvmt_list || []
        extras.forEach((nm, i) => rows.push(this.displayTrip(item, nm, i + 1)))
      })
      return rows.filter(Boolean).sort((a, b) => a.ms - b.ms)
    },
    // hide the trip column outright when the tenant's bookings carry no
    // group names; a column of dashes reads broken on a lobby tv
    displayHasGroups() {
      return this.displayRows.some(row => row.group && row.group !== '-')
    },
    isSolari() {
      return this.display_theme === 'solari'
    },
    // the solari board keeps a constant line count: unused lines flip to
    // blanks like the real hardware, and rows are keyed by position so a
    // page change flutters cells from the old text to the new
    solariRows() {
      const rows = this.currentPageRows.slice()
      while (rows.length < this.page_size) {
        rows.push({
          key: 'sf-blank-' + rows.length,
          time: '', timeDay: '', group: '', coach: '',
          pickup: '', destination: '', status: '', urgency: 'blank',
        })
      }
      return rows
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.displayRows.length / Math.max(1, this.page_size)))
    },
    currentPageRows() {
      const start = this.page_index * this.page_size
      return this.displayRows.slice(start, start + this.page_size)
    },
  },

  watch: {
    // the navbar switcher rewrites ?tenant= in place without remounting,
    // so reset the tenant-scoped state and refetch
    tenant() {
      this.window_filters = []
      this.summary = null
      this.items_full = []
      this.error = ''
      this.noData = false
      this.load_data()
    },
    // keep the page index in range and re-measure when the trip list
    // changes shape (refresh, countdown rollover, tenant switch)
    displayRows() {
      if (!this.display_mode) return
      if (this.page_index >= this.pageCount) this.page_index = 0
      this.$nextTick(this.measurePageSize)
    },
  },

  mounted() {
    this.load_data()
    // quiet auto-refresh; skips while a fetch is already in flight
    this.refresh_timer = setInterval(() => {
      if (this.refreshing) return
      this.load_data()
    }, 60000)

    window.addEventListener('keydown', this.onDisplayKeydown)
    window.addEventListener('resize', this.onDisplayResize)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)

    // kiosk bookmarks boot straight into display mode: ?display=<theme>,
    // or ?display=1 for the remembered theme
    const q = String(this.$route.query.display || '').toLowerCase()
    if (isDisplayTheme(q)) {
      this.enterDisplay(q, true)
    } else if (q === '1' || q === 'true') {
      this.enterDisplay(null, true)
    }
  },

  beforeDestroy() {
    if (this.refresh_timer) clearInterval(this.refresh_timer)
    window.removeEventListener('keydown', this.onDisplayKeydown)
    window.removeEventListener('resize', this.onDisplayResize)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
    this.stopDisplayTimers()
    document.body.classList.remove('db-display-active')
  },

  methods: {
    async load_data() {
      if (!this.tenant) return
      if (!this.board_date) this.board_date = moment().format('YYYY-MM-DD')
      const seq = ++this.fetch_seq
      this.refreshing = true
      if (!this.summary) this.loading = true
      try {
        const { value: { data = {} } = {} } = await get_trip_sheet(this.tenant, this.board_date)
        if (seq !== this.fetch_seq) return
        this.summary = data.summary || null
        this.items_full = data.results || []
        this.error = ''
        this.noData = false
        this.last_updated_at = Date.now()
      } catch (e) {
        if (seq === this.fetch_seq) {
          // the board is sourced from the telematics feed. the tripsheet
          // endpoint signals "no telematics connection" with a 400
          // (error_code 40); show that as the calm no-data fact and reserve
          // the retryable error banner for genuine failures.
          const resp = e && e.response
          const status = resp && resp.status
          const body = (resp && resp.data) || {}
          const noTelematics = status === 404 ||
            (status === 400 && (body.error_code === 40 || /telematics/i.test(body.message || '')))
          if (noTelematics) {
            this.noData = true
            this.error = ''
            this.summary = null
            this.items_full = []
          } else {
            // a genuine failure on a tenant that previously 400'd must clear
            // noData, or the calm card and the error banner stack together
            this.noData = false
            this.error = 'The departure data did not load. Check your connection and retry.'
          }
        }
      } finally {
        if (seq === this.fetch_seq) {
          this.loading = false
          this.refreshing = false
        }
      }
    },

    toggleWindowFilter(value) {
      if (this.window_filters.includes(value)) {
        this.window_filters = this.window_filters.filter(v => v !== value)
      } else {
        this.window_filters = [...this.window_filters, value]
      }
    },
    clearFilters() {
      this.window_filters = []
    },

    // minutes until departure from the epoch-seconds field, null when the
    // row has no scheduled movement
    minutesUntil(ms) {
      if (!ms || ms >= NO_MOVEMENT_EPOCH) return null
      return (ms * 1000 - Date.now()) / 60000
    },

    // soon = within 2h or overdue; today = departs later today in the
    // tenant zone; upcoming = beyond today or no time yet
    windowBucket(item) {
      const mins = this.minutesUntil(item.next_mvmt_ms)
      if (mins === null) return 'upcoming'
      if (mins <= 120) return 'soon'
      const tz = this.summary && this.summary.timezone
      const endOfDay = tz ? moment().tz(tz).endOf('day') : moment().endOf('day')
      if (item.next_mvmt_ms * 1000 <= endOfDay.valueOf()) return 'today'
      return 'upcoming'
    },

    // chip urgency keys off the real countdown, not the display string:
    // red under 45m or overdue, amber under 2h30m, gray otherwise
    deltaChipClass(ms) {
      const mins = this.minutesUntil(ms)
      if (mins === null) return 'db-delta-chip--gray'
      if (mins < 45) return 'db-delta-chip--red'
      if (mins < 150) return 'db-delta-chip--amber'
      return 'db-delta-chip--gray'
    },

    driverLabel(value) {
      if (!value) return '-'
      if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
      return String(value)
    },

    // formatDateTimeCompact's output ("6/11 7:59a") rendered in the
    // tenant zone; the shared helper formats browser-local, so the zone
    // conversion happens here, never via a global moment default
    formatStamp(value) {
      if (!value || typeof value !== 'string') return '-'
      const m = moment(String(value))
      if (!m.isValid()) return '-'
      const tz = this.summary && this.summary.timezone
      if (tz) m.tz(tz)
      return m.format('M/D h:mma').replace(/(a|p)m$/, '$1')
    },

    // departs-in sorts on the precomputed delta from the api, the display
    // string is not sortable text (mirrors the customer page)
    tableSortCompare(itemA, itemB, key) {
      if (key !== 'next_mvmt_delta') return false
      return itemA['next_mvmt_ms'] - itemB['next_mvmt_ms']
    },

    // ---- waiting room display ----

    // a moment in the tenant zone when one is configured, browser-local
    // otherwise; the board clock and stamps all route through here
    displayMoment(value) {
      const m = moment(value)
      const tz = this.summary && this.summary.timezone
      if (tz) m.tz(tz)
      return m
    },

    // maps one movement to a passenger-facing row; returns null for rows
    // the lounge should not see (no schedule, or departed over 5m ago)
    displayTrip(vehicle, mv, idx) {
      const ms = mv.next_mvmt_ms
      if (!ms || ms >= NO_MOVEMENT_EPOCH) return null
      const mins = (ms * 1000 - Date.now()) / 60000
      if (mins < -5) return null
      let status, urgency
      if (mins <= 0) {
        status = 'Departing'
        urgency = 'now'
      } else if (mins <= 25) {
        status = 'Boarding'
        urgency = 'soon'
      } else {
        status = 'in ' + this.shortCountdown(mins)
        urgency = 'later'
      }
      const stamp = this.displayTimeLabel(mv.next_mvmt_time)
      return {
        key: (vehicle.name || 'v') + '-' + idx + '-' + (mv.next_mvmt_id || ms),
        ms,
        status,
        urgency,
        time: stamp.time,
        timeDay: stamp.day,
        group: mv.next_mvmt_client_ref1 || '-',
        coach: vehicle.name || '-',
        pickup: mv.next_mvmt_pickup || mv.next_mvmt_start_base || '-',
        destination: mv.next_mvmt_destination || mv.next_mvmt_finish_base || '-',
      }
    },

    // flap-cell stamp: "SAT 6:10A" / "6:10A"; the flap component
    // uppercases on its own
    sfTime(trip) {
      if (!trip.time || trip.time === '-') return ''
      return (trip.timeDay ? trip.timeDay + ' ' : '') + trip.time
    },

    shortCountdown(mins) {
      const m = Math.max(1, Math.round(mins))
      if (m < 60) return m + ' min'
      const h = Math.floor(m / 60)
      const r = m % 60
      return r ? h + 'h ' + r + 'm' : h + (h === 1 ? ' hour' : ' hours')
    },

    // compact family stamp ("9:45a") in the tenant zone; trips beyond
    // today carry a small weekday prefix so "9:30a" cannot read as this
    // morning. returns { day, time } so the day can render de-emphasized
    displayTimeLabel(value) {
      if (!value || typeof value !== 'string') return { day: '', time: '-' }
      const m = moment(String(value))
      if (!m.isValid()) return { day: '', time: '-' }
      const tz = this.summary && this.summary.timezone
      if (tz) m.tz(tz)
      const today = this.displayMoment(Date.now())
      const sameDay = m.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
      return {
        day: sameDay ? '' : m.format('ddd'),
        time: m.format('h:mma').replace(/(a|p)m$/, '$1'),
      }
    },

    enterDisplay(theme, fromQuery) {
      const stored = localStorage.getItem(DISPLAY_THEME_KEY)
      this.display_theme = isDisplayTheme(theme) ? theme : (isDisplayTheme(stored) ? stored : 'terminal')
      this.display_mode = true
      this.page_index = 0
      this.ensureDisplayFonts()
      document.body.classList.add('db-display-active')
      this.startDisplayTimers()
      this.wakeControls()
      this.syncDisplayQuery()
      this.$nextTick(() => {
        this.measurePageSize()
        // the fullscreen api needs a user gesture, so kiosk boots via
        // query skip it (use the browser's own kiosk/fullscreen instead)
        if (!fromQuery) this.requestDisplayFullscreen()
      })
    },

    exitDisplay() {
      this.display_mode = false
      this.display_idle = false
      this.stopDisplayTimers()
      document.body.classList.remove('db-display-active')
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      }
      this.did_request_fullscreen = false
      this.syncDisplayQuery()
    },

    setDisplayTheme(value) {
      if (!isDisplayTheme(value)) return
      this.display_theme = value
      localStorage.setItem(DISPLAY_THEME_KEY, value)
      this.syncDisplayQuery()
      this.wakeControls()
    },

    // mirrors display state into ?display= so the running configuration
    // is bookmarkable for the kiosk machine
    syncDisplayQuery() {
      const current = String(this.$route.query.display || '')
      const desired = this.display_mode ? this.display_theme : ''
      if (current === desired) return
      const query = Object.assign({}, this.$route.query)
      if (desired) query.display = desired
      else delete query.display
      const nav = this.$router.replace({ query })
      if (nav && nav.catch) nav.catch(() => {})
    },

    ensureDisplayFonts() {
      if (document.getElementById('db-display-fonts')) return
      const link = document.createElement('link')
      link.id = 'db-display-fonts'
      link.rel = 'stylesheet'
      link.href = DISPLAY_FONTS_URL
      document.head.appendChild(link)
    },

    requestDisplayFullscreen() {
      const el = this.$refs.display_root
      if (!el) return
      const request = el.requestFullscreen || el.webkitRequestFullscreen
      if (!request) return
      try {
        const p = request.call(el)
        if (p && p.catch) p.catch(() => {})
        this.did_request_fullscreen = true
      } catch (e) {
        // fullscreen is best effort; the overlay already covers the page
      }
    },

    onFullscreenChange() {
      const active = document.fullscreenElement || document.webkitFullscreenElement
      if (this.display_mode && this.did_request_fullscreen && !active) {
        this.did_request_fullscreen = false
        this.exitDisplay()
      }
    },

    onDisplayKeydown(e) {
      if (!this.display_mode) return
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault()
        this.exitDisplay()
      }
    },

    wakeControls() {
      this.controls_visible = true
      this.display_idle = false
      clearTimeout(this.idle_timer)
      this.idle_timer = setTimeout(() => {
        this.controls_visible = false
        this.display_idle = true
      }, 4000)
    },

    // while the pointer rests on the pill the idle timer is suspended, so
    // it cannot fade out under a hovering cursor
    pinControls() {
      this.controls_visible = true
      this.display_idle = false
      clearTimeout(this.idle_timer)
    },

    startDisplayTimers() {
      this.stopDisplayTimers()
      this.clock_now = Date.now()
      this.clock_timer = setInterval(() => { this.clock_now = Date.now() }, 1000)
      this.minute_timer = setInterval(() => { this.minute_tick += 1 }, 30000)
      this.page_timer = setInterval(() => {
        if (this.pageCount > 1) this.page_index = (this.page_index + 1) % this.pageCount
      }, 12000)
    },

    stopDisplayTimers() {
      if (this.clock_timer) clearInterval(this.clock_timer)
      if (this.minute_timer) clearInterval(this.minute_timer)
      if (this.page_timer) clearInterval(this.page_timer)
      clearTimeout(this.idle_timer)
      clearTimeout(this.resize_timer)
      this.clock_timer = this.minute_timer = this.page_timer = null
    },

    // rows per page from the real rendered row height so any screen,
    // 720p signage to a 4k lobby panel, fills without clipping
    measurePageSize() {
      if (!this.display_mode) return
      const board = this.$refs.display_board
      if (!board) return
      const head = board.querySelector('.db-dp-row--head')
      const probe = board.querySelector('.db-dp-rows .db-dp-row, .db-dp-rows .sf-row')
      const avail = board.clientHeight - (head ? head.offsetHeight : 0)
      let rowH
      if (probe) {
        const margin = parseFloat(getComputedStyle(probe).marginBottom) || 0
        rowH = probe.offsetHeight + margin
      } else {
        rowH = Math.max(48, window.innerHeight * 0.085)
      }
      const size = Math.max(3, Math.floor(avail / Math.max(1, rowH)))
      if (size !== this.page_size) {
        this.page_size = size
        if (this.page_index >= this.pageCount) this.page_index = 0
      }
    },

    onDisplayResize() {
      if (!this.display_mode) return
      clearTimeout(this.resize_timer)
      this.resize_timer = setTimeout(this.measurePageSize, 150)
    },
  },
}
</script>

<style scoped>
.db-root {
  max-width: 1560px;
  margin: 0 auto;
  padding: 12px 32px 32px 32px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
@media (max-width: 720px) {
  .db-root { padding: 12px 16px 24px 16px; }
}

/* header rail, matching the ops family rail language */
.db-rail {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side (support-ticket rail treatment) */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 8px;
}
.db-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  min-width: 0;
}
.db-rail-eyebrow {
  font-size: 11px; line-height: 1.4;
  text-transform: uppercase; letter-spacing: 0.6px;
  color: var(--fi-ops-eyebrow); font-weight: 600;
  margin-bottom: 1px;
}
.db-rail-title {
  margin: 0; padding: 0;
  font-size: 19px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
.db-rail-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
  white-space: nowrap;
}
.db-rail-actions {
  display: inline-flex; align-items: center; gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}
/* admin tenant-context chip, same treatment as the erm rail */
.db-rail-tenant-chip {
  display: inline-flex; align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #c8d4df;
  color: var(--fi-ops-eyebrow);
  font-size: 11px; font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;
}
.db-date-input { width: 150px; }

@media (max-width: 600px) {
  .db-rail { padding: 10px 12px; gap: 10px; }
  .db-rail-title { font-size: 17px; }
  .db-rail-actions { margin-left: 0; }
  .db-date-input { width: 140px; }
}

/* below-rail staff note */
.db-note {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 12px 2px;
}

/* inline load error, advisory amber; stale rows stay visible below */
.db-error {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #7a5400;
}

/* filter chips */
.db-chip-filters {
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center;
  margin-bottom: 12px;
}
.db-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.db-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.db-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.db-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }
.db-chip:focus-visible,
.db-clear-btn:focus-visible,
.db-root .btn:focus-visible {
  outline: 2px solid var(--fi-ops-accent); outline-offset: 2px;
}

/* table card */
.db-card {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
}

/* quiet fact card for tenants without a telematics feed */
.db-no-telematics {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 44px 24px;
  text-align: center;
  color: #6b7280;
}
.db-no-telematics .fa {
  font-size: 26px;
  color: #b9c2cc;
  margin-bottom: 10px;
}
.db-no-telematics-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 4px;
}
.db-no-telematics-sub {
  font-size: 13px;
  max-width: 52ch;
  margin: 0 auto;
  line-height: 1.5;
}
.db-card >>> .table.b-table { margin-bottom: 0; }

/* vertical scrollport on the responsive wrapper so the sticky thead has
   the scroll container it pins against; scrolling owns overflow, there
   is no per-page cap */
.db-card >>> .table-responsive {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  margin-bottom: 0;
}

/* the canonical dense table pattern; deep selectors because bootstrap-vue
   renders the inner table. no font-size on the table itself so the
   wall-display size set on the card inherits into the cells */
.db-root >>> .table.b-table { border-collapse: collapse; }
.db-root >>> .table.b-table th,
.db-root >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.db-root >>> .table.b-table th:first-child,
.db-root >>> .table.b-table td:first-child { padding-left: 12px !important; }
.db-root >>> .table.b-table th:last-child,
.db-root >>> .table.b-table td:last-child   { padding-right: 12px !important; }
.db-root >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.db-root >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }

.db-root >>> th[aria-sort] { background-image: none !important; padding-right: 0.75rem !important; white-space: nowrap; }
.db-root >>> th[aria-sort]::before { display: none !important; }
.db-root >>> th[aria-sort]::after  { display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999; vertical-align: middle; float: none !important; width: auto !important; height: auto !important; opacity: 1 !important; }
.db-root >>> th[aria-sort="none"]::after       { content: "\2195" !important; }
.db-root >>> th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
.db-root >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* busy / empty states */
.db-busy {
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}
.db-empty {
  padding: 14px 12px;
  font-size: 13px;
  color: #6b7280;
}
.db-clear-btn {
  background: none; border: none; padding: 0; margin-left: 6px;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
}
.db-clear-btn:hover { color: var(--fi-ops-accent-deep); }
.db-empty-reload { margin-left: 8px; }

/* one movement per line so stacked bookings align across the four
   movement columns; the fixed line height keeps text and chip lines
   level with each other */
.db-mvmt-line {
  display: block;
  white-space: nowrap;
  line-height: 22px;
}

/* departs-in chips, spec trio colors */
.db-delta-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  border: 1px solid;
  white-space: nowrap;
  vertical-align: middle;
}
.db-delta-chip--red   { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.db-delta-chip--amber { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.db-delta-chip--gray  { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }

/* long locations truncate to one line, full text on the tooltip */
.db-trunc {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* ============================================================
   waiting room display
   a fixed overlay sized for tvs: vh-driven type so 720p signage and
   4k lobby panels both fill, themes swap css variables only
   ============================================================ */
.db-display {
  position: fixed;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 4000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--dp-bg);
  color: var(--dp-ink);
  font-family: var(--dp-body);
}
.db-display--idle { cursor: none; }

.db-dp-frame {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: clamp(18px, 3vh, 42px) clamp(22px, 3.4vw, 64px) clamp(14px, 2.2vh, 30px);
}

/* header: operator + title left, station clock right */
.db-dp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  padding-bottom: clamp(10px, 1.8vh, 22px);
  border-bottom: var(--dp-head-rule);
}
.db-dp-operator {
  font-size: clamp(12px, 2vh, 24px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--dp-accent);
  margin-bottom: clamp(2px, 0.6vh, 8px);
}
.db-dp-title {
  margin: 0;
  font-family: var(--dp-display);
  font-size: clamp(30px, 6.4vh, 86px);
  font-weight: 700;
  line-height: 1;
  letter-spacing: var(--dp-title-tracking);
  text-transform: uppercase;
}
.db-dp-head-right { text-align: right; flex-shrink: 0; }
.db-dp-clock {
  font-family: var(--dp-mono);
  font-variant-numeric: tabular-nums;
  font-size: clamp(28px, 5.8vh, 78px);
  font-weight: 600;
  line-height: 1;
}
.db-dp-date {
  font-size: clamp(12px, 1.9vh, 24px);
  color: var(--dp-muted);
  margin-top: clamp(2px, 0.6vh, 8px);
}
.db-dp-tz { letter-spacing: 0.04em; }

/* board grid: departs / trip / destination / pickup / coach / status */
.db-dp-board {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: clamp(8px, 1.4vh, 18px);
}
.db-dp-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(136px, 0.9fr) 1.4fr 1.5fr 1.1fr minmax(96px, 0.7fr) minmax(118px, 0.95fr);
  gap: clamp(10px, 1.3vw, 28px);
  align-items: center;
  min-height: clamp(46px, 8.4vh, 104px);
  padding: 0 clamp(6px, 0.8vw, 18px);
  font-size: var(--dp-row-font);
}
/* tenants without group names on bookings run a five-column board */
.db-display--no-trip .db-dp-row {
  grid-template-columns: minmax(136px, 0.9fr) 1.7fr 1.3fr minmax(96px, 0.8fr) minmax(118px, 0.95fr);
}
.db-display--no-trip .db-dp-cell-trip { display: none; }
.db-dp-row > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.db-dp-row--head {
  min-height: 0;
  padding-top: 0;
  padding-bottom: clamp(5px, 1vh, 12px);
  font-size: clamp(11px, 1.7vh, 20px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--dp-muted);
  border-bottom: var(--dp-rule);
  background: none;
}
.db-dp-rows { flex: 1; min-height: 0; overflow: hidden; }
.db-dp-rows .db-dp-row {
  border-bottom: var(--dp-rule);
  animation: var(--dp-row-anim);
  animation-fill-mode: both;
}

.db-dp-cell-time {
  font-family: var(--dp-mono);
  font-variant-numeric: tabular-nums;
  font-size: var(--dp-time-font);
  font-weight: 600;
}
.db-dp-time-day {
  font-size: 0.52em;
  font-weight: 600;
  color: var(--dp-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-right: 0.5em;
}
.db-dp-cell-trip { font-weight: 700; }
.db-dp-cell-coach { color: var(--dp-muted); font-family: var(--dp-mono); }
.db-dp-cell-status { text-align: right; }
.db-dp-row--head .db-dp-cell-status { text-align: right; }

.db-dp-status {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: clamp(13px, 2.3vh, 30px);
  font-weight: 700;
}
.db-dp-status--now,
.db-dp-status--soon { text-transform: uppercase; letter-spacing: 0.08em; }
.db-dp-status--now { color: var(--dp-now); }
.db-dp-status--soon { color: var(--dp-soon); }
.db-dp-status--later { color: var(--dp-muted); font-weight: 600; }

/* quiet lounge: nothing scheduled */
.db-dp-quiet {
  padding: 14vh 24px;
  text-align: center;
}
.db-dp-quiet-title {
  font-family: var(--dp-display);
  font-size: clamp(24px, 5vh, 64px);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--dp-title-tracking);
}
.db-dp-quiet-sub {
  margin-top: clamp(6px, 1.4vh, 16px);
  font-size: clamp(14px, 2.4vh, 30px);
  color: var(--dp-muted);
}

/* footer: page dots left, brand + freshness right */
.db-dp-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: clamp(8px, 1.5vh, 18px);
  border-top: var(--dp-rule);
}
.db-dp-pages { display: inline-flex; gap: 8px; }
.db-dp-page-dot {
  width: clamp(8px, 1.2vh, 14px);
  height: clamp(8px, 1.2vh, 14px);
  border-radius: 50%;
  border: 1px solid var(--dp-muted);
  opacity: 0.55;
}
.db-dp-page-dot--on { background: var(--dp-accent); border-color: var(--dp-accent); opacity: 1; }
.db-dp-brand {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: clamp(10px, 1.6vh, 18px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--dp-muted);
}
.db-dp-updated { letter-spacing: 0.08em; text-transform: none; }
.db-dp-live-dot {
  width: clamp(6px, 1vh, 11px);
  height: clamp(6px, 1vh, 11px);
  border-radius: 50%;
  background: var(--dp-accent);
  animation: db-dp-pulse 2.6s ease-in-out infinite;
}

/* auto-hiding control pill */
.db-dp-controls {
  position: absolute;
  left: 50%;
  bottom: clamp(20px, 4vh, 44px);
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(9, 12, 17, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  color: #fff;
  transition: opacity 0.35s ease;
}
.db-dp-controls--hidden { opacity: 0; pointer-events: none; }
.db-dp-controls-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.65);
}
.db-dp-theme-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  border: 2px solid transparent;
  transition: box-shadow 0.15s, transform 0.15s;
}
.db-dp-theme-dot:hover { transform: scale(1.12); }
.db-dp-theme-dot--terminal { background: #10151d; border-color: #f5b942; }
.db-dp-theme-dot--ember { background: #060402; border-color: #ffb000; }
.db-dp-theme-dot--daylight { background: #faf8f2; border-color: #2e6e9e; }
.db-dp-theme-dot--depot { background: #f5ede0; border-color: #b87a3d; }
.db-dp-theme-dot--solari { background: #101115; border-color: #f4f1e8; }
.db-dp-theme-dot--on { box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55); }
.db-dp-theme-dot:focus-visible,
.db-dp-exit:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.db-dp-controls-sep {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.25);
}
.db-dp-exit {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  cursor: pointer;
  transition: background 0.15s;
}
.db-dp-exit:hover { background: rgba(255, 255, 255, 0.14); }

/* ---- theme: terminal (split-flap homage) ---- */
.db-display--terminal {
  --dp-display: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --dp-body: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --dp-mono: 'IBM Plex Mono', Menlo, Consolas, monospace;
  --dp-bg: radial-gradient(120% 95% at 50% 0%, #1b2330 0%, #0d1219 55%, #080b10 100%);
  --dp-ink: #f3efe2;
  --dp-muted: #8d97a6;
  --dp-accent: #f5b942;
  --dp-now: #ff6a55;
  --dp-soon: #f5b942;
  --dp-rule: 1px solid rgba(243, 239, 226, 0.12);
  --dp-head-rule: 2px solid rgba(243, 239, 226, 0.22);
  --dp-title-tracking: 0.12em;
  --dp-row-font: clamp(18px, 3.2vh, 42px);
  --dp-time-font: clamp(20px, 3.5vh, 46px);
  --dp-row-anim: db-dp-flap 0.5s cubic-bezier(0.2, 0.7, 0.3, 1);
}
/* each row reads as a flap line card with the signature split hairline */
.db-display--terminal .db-dp-rows .db-dp-row {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.055) 0%, rgba(255, 255, 255, 0.018) 100%);
  border-radius: 6px;
  border-bottom: none;
  margin-bottom: clamp(3px, 0.7vh, 9px);
}
.db-display--terminal .db-dp-rows .db-dp-row::after {
  content: '';
  position: absolute;
  left: 0; right: 0; top: 50%;
  height: 1px;
  background: rgba(0, 0, 0, 0.38);
  pointer-events: none;
}
.db-display--terminal .db-dp-cell-trip,
.db-display--terminal .db-dp-cell-dest,
.db-display--terminal .db-dp-cell-pickup,
.db-display--terminal .db-dp-cell-coach {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.db-display--terminal .db-dp-cell-time { color: var(--dp-accent); }
.db-display--terminal .db-dp-status--now { animation: db-dp-soft-blink 1.6s ease-in-out infinite; }

/* ---- theme: ember (amber led phosphor) ---- */
.db-display--ember {
  --dp-display: 'Share Tech Mono', Menlo, monospace;
  --dp-body: 'Share Tech Mono', Menlo, monospace;
  --dp-mono: 'Share Tech Mono', Menlo, monospace;
  --dp-bg: radial-gradient(110% 80% at 50% 12%, #181006 0%, #0a0603 52%, #050302 100%);
  --dp-ink: #ffb000;
  --dp-muted: rgba(255, 176, 0, 0.48);
  --dp-accent: #ffb000;
  --dp-now: #ff3b30;
  --dp-soon: #ffd34d;
  --dp-rule: 1px solid rgba(255, 176, 0, 0.18);
  --dp-head-rule: 2px solid rgba(255, 176, 0, 0.32);
  --dp-title-tracking: 0.18em;
  --dp-row-font: clamp(16px, 2.9vh, 38px);
  --dp-time-font: clamp(17px, 3vh, 40px);
  --dp-row-anim: db-dp-flicker 0.5s ease-out;
}
/* crt scanlines over the whole panel */
.db-display--ember::before {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 2;
  pointer-events: none;
  background: repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.26) 0px, rgba(0, 0, 0, 0.26) 2px, transparent 2px, transparent 5px);
}
.db-display--ember .db-dp-row span,
.db-display--ember .db-dp-title,
.db-display--ember .db-dp-clock {
  text-shadow: 0 0 14px rgba(255, 176, 0, 0.4);
}
.db-display--ember .db-dp-row { text-transform: uppercase; }
.db-display--ember .db-dp-status--now {
  text-shadow: 0 0 16px rgba(255, 59, 48, 0.65);
  animation: db-dp-led-blink 1.1s steps(2, jump-none) infinite;
}
.db-display--ember .db-dp-status--soon { text-shadow: 0 0 14px rgba(255, 211, 77, 0.55); }

/* ---- theme: daylight (paper and ink lobby board) ---- */
.db-display--daylight {
  --dp-display: 'Archivo', 'Helvetica Neue', Arial, sans-serif;
  --dp-body: 'Archivo', 'Helvetica Neue', Arial, sans-serif;
  --dp-mono: 'Archivo', 'Helvetica Neue', Arial, sans-serif;
  --dp-bg: linear-gradient(180deg, #fdfcf8 0%, #f4f1e9 100%);
  --dp-ink: #16243b;
  --dp-muted: #707a89;
  --dp-accent: #2e6e9e;
  --dp-now: #b91c1c;
  --dp-soon: #8a5d00;
  --dp-rule: 1px solid #e3ded1;
  --dp-head-rule: 3px solid #16243b;
  --dp-title-tracking: -0.01em;
  --dp-row-font: clamp(17px, 3vh, 40px);
  --dp-time-font: clamp(19px, 3.3vh, 44px);
  --dp-row-anim: db-dp-fade-up 0.45s ease;
}
.db-display--daylight .db-dp-title { text-transform: none; font-weight: 800; }
.db-display--daylight .db-dp-cell-time { font-variant-numeric: tabular-nums; color: var(--dp-accent); }
.db-display--daylight .db-dp-status--now,
.db-display--daylight .db-dp-status--soon {
  border: 2px solid currentColor;
  border-radius: 999px;
  padding: 0.1em 0.7em;
}
.db-display--daylight { --dp-featured-bg: rgba(46, 110, 158, 0.07); }

/* ---- theme: depot (paper and brass, after vsm-waitingroom.html) ---- */
.db-display--depot {
  --dp-display: 'Fraunces', Georgia, serif;
  --dp-body: 'Manrope', 'Segoe UI', system-ui, sans-serif;
  --dp-mono: 'Fraunces', Georgia, serif;
  --dp-bg: #f5ede0;
  --dp-ink: #1a1d2e;
  --dp-muted: #8b8675;
  --dp-accent: #b87a3d;
  --dp-now: #8b5a2b;
  --dp-soon: #1976d2;
  --dp-rule: 1px solid rgba(26, 29, 46, 0.10);
  --dp-head-rule: 1px solid rgba(26, 29, 46, 0.20);
  --dp-title-tracking: -0.02em;
  --dp-row-font: clamp(17px, 3vh, 40px);
  --dp-time-font: clamp(19px, 3.4vh, 46px);
  --dp-row-anim: db-dp-flap 0.5s cubic-bezier(0.2, 0.7, 0.3, 1);
  --dp-featured-bg: linear-gradient(90deg, rgba(184, 122, 61, 0.10) 0%, rgba(184, 122, 61, 0.02) 100%);
}
/* paper grain + warm ambient washes, both under the content layer */
.db-display--depot::before {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0 0.10 0 0 0 0.07 0'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>");
  mix-blend-mode: multiply;
  opacity: 0.7;
}
.db-display--depot::after {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 60% 50% at 50% 0%, rgba(184, 122, 61, 0.10), transparent 60%),
    radial-gradient(ellipse 80% 60% at 50% 100%, rgba(25, 118, 210, 0.04), transparent 60%);
}
.db-display--depot .db-dp-title {
  text-transform: none;
  font-weight: 400;
  font-variation-settings: 'opsz' 144, 'SOFT' 100;
}
.db-display--depot .db-dp-operator { color: #8b5a2b; }
.db-display--depot .db-dp-clock {
  font-weight: 300;
  letter-spacing: -0.04em;
  font-variation-settings: 'opsz' 144, 'SOFT' 30;
}
.db-display--depot .db-dp-cell-time {
  font-weight: 400;
  font-variation-settings: 'opsz' 144, 'SOFT' 50;
}
.db-display--depot .db-dp-cell-trip {
  font-family: var(--dp-display);
  font-weight: 500;
  font-variation-settings: 'opsz' 60, 'SOFT' 50;
}
.db-display--depot .db-dp-cell-coach {
  color: #5a5849;
  font-variation-settings: 'opsz' 60, 'SOFT' 50;
}
/* status pills with the mockup's dot, pill chrome, and boarding glow */
.db-display--depot .db-dp-status {
  border: 1px solid rgba(26, 29, 46, 0.20);
  border-radius: 999px;
  padding: 0.18em 0.8em;
  background: #ede4d3;
  font-family: var(--dp-body);
  font-size: clamp(12px, 2vh, 26px);
}
.db-display--depot .db-dp-status::before {
  content: '';
  display: inline-block;
  width: 0.5em;
  height: 0.5em;
  border-radius: 50%;
  background: currentColor;
  margin-right: 0.55em;
}
.db-display--depot .db-dp-status--now {
  color: #8b5a2b;
  background: rgba(184, 122, 61, 0.10);
  border-color: rgba(184, 122, 61, 0.30);
  animation: db-dp-soft-blink 1.6s ease-in-out infinite;
}
.db-display--depot .db-dp-status--soon {
  color: #fff;
  background: #1976d2;
  border-color: #1976d2;
  animation: db-dp-glow 2.4s infinite ease-in-out;
}
.db-display--depot .db-dp-status--later { color: #5a5849; }

/* ---- theme: solari (true animated split-flap) ---- */
.db-display--solari {
  --dp-display: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --dp-body: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --dp-mono: 'IBM Plex Mono', Menlo, Consolas, monospace;
  --dp-bg: radial-gradient(110% 85% at 50% 8%, #17181d 0%, #0b0c10 60%, #07080b 100%);
  --dp-ink: #f4f1e8;
  --dp-muted: #79808c;
  --dp-accent: #f5b942;
  --dp-now: #ff6a55;
  --dp-soon: #f5b942;
  --dp-rule: 1px solid rgba(244, 241, 232, 0.10);
  --dp-head-rule: 2px solid rgba(244, 241, 232, 0.20);
  --dp-title-tracking: 0.14em;
  --dp-row-font: clamp(15px, 2.6vh, 36px);
  --dp-time-font: clamp(15px, 2.6vh, 36px);
  --dp-row-anim: none;
  --dp-featured-bg: rgba(245, 185, 66, 0.06);
}
.db-display--solari .db-dp-clock--flap {
  font-size: clamp(20px, 4.4vh, 60px);
  font-family: var(--dp-display);
}
/* flap line layout: fixed-width columns so every row and the printed
   header align; the em widths mirror split_flap's cell metrics
   (0.66em cell + 0.06em gap) - keep in sync */
.sf-head,
.sf-row {
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.6vw, 34px);
  font-size: var(--dp-row-font);
  padding: clamp(4px, 0.9vh, 12px) clamp(6px, 0.8vw, 18px);
  position: relative;
}
.sf-head { padding-top: 0; }
.sf-col { display: inline-flex; }
.sf-col-time   { min-width: calc(9 * 0.66em + 8 * 0.06em); }
.sf-col-group  { min-width: calc(12 * 0.66em + 11 * 0.06em); }
.sf-col-dest   { min-width: calc(13 * 0.66em + 12 * 0.06em); }
.sf-col-pickup { min-width: calc(12 * 0.66em + 11 * 0.06em); }
.sf-col-coach  { min-width: calc(8 * 0.66em + 7 * 0.06em); }
.sf-col-status { min-width: calc(10 * 0.66em + 9 * 0.06em); margin-left: auto; justify-content: flex-end; }
.sf-head-label {
  font-size: clamp(11px, 1.7vh, 20px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--dp-muted);
}
.sf-row { border-bottom: var(--dp-rule); }
.sf-status--now { color: var(--dp-now); }
.sf-status--soon { color: var(--dp-soon); }
.sf-status--later { color: #d8d4c8; }
.sf-status--blank { color: var(--dp-muted); }
.db-dp-rows--solari { position: relative; }
.db-dp-quiet--solari {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  padding: 0 24px;
}

/* featured next-departure row: accent bar + theme tint (mockup motif) */
.db-dp-rows .db-dp-row--next { background: var(--dp-featured-bg, transparent); }
.db-dp-rows .db-dp-row--next::before {
  content: '';
  position: absolute;
  left: 0;
  top: 14%;
  bottom: 14%;
  width: 3px;
  background: var(--dp-accent);
}
.db-display--terminal .db-dp-rows .db-dp-row--next {
  background: linear-gradient(180deg, rgba(245, 185, 66, 0.16) 0%, rgba(245, 185, 66, 0.05) 100%);
}
.db-display--ember { --dp-featured-bg: rgba(255, 176, 0, 0.07); }

/* page-rotation progress bar; keep in sync with the 12s page_timer */
.db-dp-rotbar {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 3px;
  z-index: 5;
}
.db-dp-rotbar-fill {
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, var(--dp-accent), var(--dp-now));
  animation: db-dp-rotate-progress 12s linear forwards;
}

/* narrower signage drops the pickup column before anything truncates */
@media (max-width: 1100px) {
  .db-dp-row { grid-template-columns: minmax(116px, 0.9fr) 1.5fr 1.5fr minmax(84px, 0.6fr) minmax(110px, 0.9fr); }
  .db-display--no-trip .db-dp-row { grid-template-columns: minmax(116px, 0.9fr) 1.8fr minmax(84px, 0.7fr) minmax(110px, 0.9fr); }
  .db-dp-cell-pickup { display: none; }
}

@keyframes db-dp-flap {
  0% { transform: perspective(900px) rotateX(-68deg); transform-origin: 50% 0; opacity: 0; }
  60% { opacity: 1; }
  100% { transform: perspective(900px) rotateX(0deg); transform-origin: 50% 0; opacity: 1; }
}
@keyframes db-dp-fade-up {
  from { transform: translateY(14px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@keyframes db-dp-flicker {
  0% { opacity: 0; }
  18% { opacity: 0.85; }
  26% { opacity: 0.2; }
  38% { opacity: 1; }
  52% { opacity: 0.6; }
  100% { opacity: 1; }
}
@keyframes db-dp-led-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}
@keyframes db-dp-soft-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
@keyframes db-dp-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@keyframes db-dp-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.45); }
  50% { box-shadow: 0 0 0 8px rgba(25, 118, 210, 0); }
}
@keyframes db-dp-rotate-progress {
  from { width: 0; }
  to { width: 100%; }
}
</style>

<style>
/* waiting room display: page chrome that must hide lives outside this
   component's scope; the body class is set while display mode is active */
body.db-display-active { overflow: hidden; }
body.db-display-active #navbar,
body.db-display-active .fa-launcher-root { display: none !important; }
</style>
